import React, { useEffect, useState } from "react";
import { Plus, X, Edit2, Check } from "lucide-react";
import { useAppointmentSaleDetails } from "../hooks/useAppointmentSaleDetails";
import { differenceInMinutes, format, isSameDay } from "date-fns";
import { useUpdateAppointment } from "../hooks/useUpdateAppointment";
import { useUpdateAppointmentNew } from "../hooks/useUpdateAppointmentNew";
import { ServiceModal } from "../modals/ServiceModal";
import ProductModal from "../modals/ProductModal";
import { useSelector } from "react-redux";
import { selectProducts } from "../../../redux/productSlice";
import { selectAccount } from "../../../redux/accountSlice";
import { calculateCheckoutTaxCents, getCheckoutBreakdown, getPaidSaleSummary, paidExtrasLabel } from "../../../utils/checkoutBreakdown";
import { formatLocalTime } from "../../../utils/timezone";
import { calendarDateFromUrl } from "../../../utils/formatTime";
import http from "app/requests/axiosInstance";
import AvailableSlotSelect from "../ui/AvailableSlotSelect";
import { MyCalendarEvent } from "../hooks/useCalendarState";
import { toast } from "react-toastify";
import { selectUser, UserRoles } from "../../../redux/userSlice";
import { swapServicesToEmployee } from "../utils/swapServicesToEmployee";
import { getProviderOptions } from "../utils/getProviderOptions";
import { hasAppointmentStarted } from "../utils/hasAppointmentStarted";

interface CheckoutItem {
    id: string | number;
    name: string;
    type: "service" | "product";
    category?: string;
    practitioner?: string;
    price: number;
    quantity: number;
}

interface Appointment {
    id: number;
    patientName: string;
    practitioner: string;
    startTime: string;
    endTime: string;
    duration: number;
    createdAt?: Date;
    notes?: string;
}

interface CheckoutPanelProps {
    show: boolean;
    onClose: () => void;
    selectedAppointmentEvent: any;
    setSelectedAppointmentEvent: any;
    proceedToPayment: any

    getSchedulesOfEmployees: any
    toggleAppointmentDrawer: any
    setPendingAppointment: any
    closeDrawer: any
    onMakeAppointmentRepeating?: any
    onStopAppointmentRepeating?: any
    onEditAppointmentRepeating: any
    setAppointmentDrawer: any
    setAddServiceModal: () => void
    setAddProductModal: () => void
    updateCalendarData?: any
    events: MyCalendarEvent[]
}

const CheckoutPanel: React.FC<CheckoutPanelProps> = ({
    setAppointmentDrawer,
    getSchedulesOfEmployees,
    toggleAppointmentDrawer,
    setPendingAppointment,
    closeDrawer,
    onMakeAppointmentRepeating,
    onStopAppointmentRepeating,
    onEditAppointmentRepeating,

    setSelectedAppointmentEvent,
    show,
    onClose,
    selectedAppointmentEvent,
    proceedToPayment,
    updateCalendarData,
    events,

}) => {

    const {
        isEditing,
        setIsEditing,
        employees,
        onUpdateAppointment,
        selectedClient,

        setSelectedClient,
        onHandleClientChange,
        clients,
        selectedServices,
        editingEmployeeInServiceIndex,
        setEditingEmployeeInServiceIndex,
        employeeSelectRef,
        handleEmployeeChange,
        serviceSelectValue,
        onHandleAddService,
        availableServices,
        selectedDate,
        setSelectedDate,
        isDateInputFocused,
        isRepeating,
        setIsDateInputFocused,
        onRemoveService,
        cancelEditing,
        onDeleteAppointmentGroup,
        handleClientSearch,
        onTimeChange,
        onDateChange,
        handleStopRepeating,
        handleMakeRepeating,
        specificAppointmentGroup, // isSaving,
        saleForSelectedAppointment,
        handleUpdateAppointment,
        updatingAppointment,
        setUpdatingAppointment
    } = useUpdateAppointment({
        selectedAppointmentEvent,
        getSchedulesOfEmployees,
        toggleAppointmentDrawer,
        setPendingAppointment,
        onMakeAppointmentRepeating,
        onStopAppointmentRepeating,
        onEditAppointmentRepeating,
        closeDrawer,
    });


    const [addServiceModal, setAddServiceModal] = useState(false)
    const [addProductModal, setAddProductModal] = useState(false)

    const saleDetails = useAppointmentSaleDetails(selectedAppointmentEvent);
    const { isClosed, isOpen, paid, pending, hasBalanceDue } = saleDetails ?? {};
    const showAsClosed = Boolean(isClosed || ((paid ?? 0) > 0 && !hasBalanceDue));


    const {

        draft,
        isSaving,
        isEditable,
        updateDate,
        updateTime,
        updateServices,
        updateProducts,
        updateNotes,
        changeEmployee,
        removeService,
        removeWholeProduct,
        removeProduct,
        addService,
        addProduct,

    } = useUpdateAppointmentNew({
        selectedAppointmentEvent,
        setSelectedAppointmentEvent,
        getSchedulesOfEmployees,
    });


    const [checkoutAppointment, setCheckoutAppointment] = useState<Appointment | null>(null);
    const [checkoutItems, setCheckoutItems] = useState<CheckoutItem[]>([]);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [showNoShowConfirm, setShowNoShowConfirm] = useState(false);
    const [waiveNoShowFee, setWaiveNoShowFee] = useState(false);
    const [markingNoShow, setMarkingNoShow] = useState(false);
    const [isEditingNotes, setIsEditingNotes] = useState(false);

    // lateFee: inside the shop's cutoff, staff cancelling for a client who called charge or (admins) waive
    // the shop's late-cancel fee
    const deleteAppointment = async (appointmentId: number, lateFee?: "charge" | "waive") => {
        try {
            const { data } = await http.patch(`/v1/appointments/${appointmentId}/cancel`, lateFee ? { lateFee } : undefined);
            const charge = data?.charge;
            if (charge?.status === "charged") toast.success(`Cancelled. Charged a ${formatCents(charge.amountCents)} late-cancel fee.`);
            else if (charge?.status === "waived") toast.success("Cancelled. The late-cancel fee was waived.");
            else if (charge?.status === "no_card") toast.warn("Cancelled. There's no card on file, so the late-cancel fee wasn't charged.");
            else if (charge?.status === "declined") toast.error(`Cancelled, but the card was declined: ${charge.reason}.`);
            const calendarViewDate = calendarDateFromUrl();

            if (updateCalendarData) {
                await updateCalendarData(calendarViewDate);
            } else if (getSchedulesOfEmployees) {
                await getSchedulesOfEmployees({ currentDate: calendarViewDate });
            }
            onClose();
        } catch (error: any) {
            toast.error(error?.data?.error || "Couldn't cancel this appointment.");
            console.error("Error cancelling appointment:", error);
        }
    };

    const refreshCalendar = async () => {
        const calendarViewDate = calendarDateFromUrl();
        if (updateCalendarData) {
            await updateCalendarData(calendarViewDate);
        } else if (getSchedulesOfEmployees) {
            await getSchedulesOfEmployees({ currentDate: calendarViewDate });
        }
    };

    // Marks the whole booking a no-show. The server applies the shop's no-show policy and says what it charged.
    const markNoShow = async (appointmentId: number) => {
        setMarkingNoShow(true);
        try {
            const { data } = await http.post(`/v1/appointments/${appointmentId}/no_show`, {
                waiveFee: waiveNoShowFee,
            });
            const charge = data?.charge ?? {};
            const amount = formatCents(charge.amountCents ?? 0);
            switch (charge.status) {
                case "charged":
                    toast.success(`Marked as a no-show. Charged ${amount} to the card on file.`);
                    break;
                case "waived":
                    toast.success("Marked as a no-show. The fee was waived.");
                    break;
                case "covered":
                    toast.success("Marked as a no-show. The deposit covers the fee.");
                    break;
                case "no_card":
                    toast.warn("Marked as a no-show. There's no card on file, so collect the fee at checkout.");
                    break;
                case "declined":
                    toast.error(`Marked as a no-show, but the card was declined: ${charge.reason}. Collect the fee at checkout.`);
                    break;
                default:
                    toast.success("Marked as a no-show.");
            }
            await refreshCalendar();
            onClose();
        } catch (error: any) {
            toast.error(error?.data?.error || "Couldn't mark this appointment as a no-show.");
        } finally {
            setMarkingNoShow(false);
            setShowNoShowConfirm(false);
            setWaiveNoShowFee(false);
        }
    };

    const formatDateLocal = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const account: any = useSelector(selectAccount);

    const calculateSubtotal = (): number => {
        return checkoutItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    };

    // Same Tax / Total / Paid / Pending lines as the payment panel, so the
    // drawer's numbers add up and match what checkout will charge.
    const itemsCents = (type: "service" | "product") =>
        Math.round(
            checkoutItems
                .filter((item) => item.type === type)
                .reduce((sum, item) => sum + item.price * item.quantity, 0) * 100
        );
    const appointmentData: any = selectedAppointmentEvent?.appointment ?? selectedAppointmentEvent;
    const servicesCents = itemsCents("service");
    const productsCents = itemsCents("product");
    const checkoutTaxCents = calculateCheckoutTaxCents({
        servicesCents,
        productsCents,
        taxPercentage: appointmentData?.shop?.taxPercentage,
        applyToServices: account?.applyTaxToService,
        applyToProducts: account?.applyTaxToProduct,
    });
    const breakdown = getCheckoutBreakdown({
        subtotalCents: servicesCents + productsCents,
        taxCents: checkoutTaxCents,
        sale: selectedAppointmentEvent?.sale ?? appointmentData?.sale,
        account,
    });
    const formatCents = (cents: number) => `$${(cents / 100).toFixed(2)}`;
    const closedSale = selectedAppointmentEvent?.sale ?? appointmentData?.sale;
    const paidSummary = closedSale ? getPaidSaleSummary(closedSale) : null;

    const updateItemQuantity = (id: string | number, type: string, newQty: number) => {
        setCheckoutItems((prev) => prev.map((item) => item.id === id && item.type === type ? {
            ...item,
            quantity: Math.max(1, newQty)
        } : item));
    };

    const removeItemFromCheckout = (item: any, type: string) => {
        if (type === 'service') {
            removeService(item);
        } else {
            removeWholeProduct(item);
        }
    };

    const generateTimeSlots = (start: string, end: string, interval = 15) => {
        const slots: string[] = [];
        const toMinutes = (t: string) => {
            const [h, m] = t.split(":").map(Number);
            return h * 60 + m;
        };
        let current = toMinutes(start);
        const endMinutes = toMinutes(end);
        while (current <= endMinutes) {
            const h = Math.floor(current / 60);
            const m = current % 60;
            slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
            current += interval;
        }
        return slots;
    };

    const formatToAmPm = (time24: string) => {
        const [h, m] = time24.split(":").map(Number);
        const period = h >= 12 ? "PM" : "AM";
        const hour12 = h % 12 === 0 ? 12 : h % 12;
        return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
    };

    useEffect(() => {
        if (!selectedAppointmentEvent || !selectedAppointmentEvent.appointment) return;

        const appt = selectedAppointmentEvent.appointment;
        const client = appt.client;
        const services = appt.services || [];
        const apptProducts = appt.products || [];

        // Construct appointment info.
        // formatLocalTime() converts UTC ISO string → shop timezone display time (not UTC, not system TZ)
        const newAppointment: Appointment = {
            id: appt.id,
            patientName: client?.name || "Unknown",
            practitioner: services[0]?.employee?.name || "Unknown",
            startTime: formatLocalTime(appt.startTime),
            endTime: formatLocalTime(appt.endTime),
            duration: appt.duration || 0,
            createdAt: client?.createdAt,
            notes: appt.notes ?? "",
        };

        // Map services to checkout items
        const serviceItems: CheckoutItem[] = services.map((s: any) => ({
            id: s.id,
            name: s.title,
            type: "service" as const,
            category: s.serviceCategory?.name,
            practitioner: s.employee?.name,
            price: s.priceCents / 100,
            quantity: 1,
        }));
        const productItems: CheckoutItem[] = apptProducts.map((s: any) => ({
            id: s.product.id,
            name: s.product.title,
            type: "product" as const,
            category: s.product.productCategory?.name,
            price: s.product.priceCents / 100,
            quantity: s.quantity,
        }));

        setCheckoutAppointment(newAppointment);
        setCheckoutItems([...serviceItems, ...productItems]);
    }, [selectedAppointmentEvent]);


    const products = useSelector(selectProducts);



    const handleServiceModal = (val: boolean) => {
        setAddServiceModal(val)
    }
    const handleProductModal = (val: boolean) => {
        setAddProductModal(val)
    }



    const selectedEmployee = employees.find(
        (e) => e.id === draft?.employee?.id
    );

    // Only admins move appointments between barbers, and only while the sale is still open
    const currentUser = useSelector(selectUser);
    const canChangeProvider = currentUser?.role === UserRoles.ADMIN && !showAsClosed;

    // A no-show can only be recorded once the appointment has started, and only once
    const isNoShow = selectedAppointmentEvent?.appointment?.status === "no_show";
    // From the appointment's real start instant, not the event's shop-wall-clock `start`
    const hasStarted = hasAppointmentStarted(selectedAppointmentEvent?.appointment?.startTime);
    const canMarkNoShow = hasStarted && !isNoShow;
    const canWaiveNoShowFee = currentUser?.role === UserRoles.ADMIN;
    // A client calling to cancel this close to the start is a late cancel under the shop's policy
    const realStart = selectedAppointmentEvent?.appointment?.startTime;
    const isInsideCutoff = !!realStart &&
        Date.now() >= new Date(realStart).getTime() - (account?.clientChangeCutoffHours ?? 24) * 3600 * 1000;
    const hasLateCancelFee = isInsideCutoff && !!account?.lateCancelPolicy && account.lateCancelPolicy !== "NONE";
    const lateCancelText = account?.lateCancelPolicy === "FIXED_FEE"
        ? `the ${formatCents(account?.lateCancelFeeCents ?? 0)} late-cancel fee`
        : "the full amount as a late-cancel fee";

    const noShowPolicyText = (() => {
        switch (account?.noShowPolicy) {
            case "FULL_AMOUNT":
                return "The card on file will be charged the full amount, less any deposit.";
            case "FIXED_FEE":
                return `The card on file will be charged the ${formatCents(account?.noShowFeeCents ?? 0)} no-show fee, less any deposit.`;
            default:
                return "Nothing will be charged. Any deposit is kept.";
        }
    })();

    // The calendar's events only cover the day it shows, so availability is checked only
    // while the appointment is still on that day.
    const providerOptions = draft
        ? getProviderOptions({
            employees,
            services: draft.services,
            start: draft.startTime,
            end: draft.endTime,
            events,
            appointmentId: draft.appointmentId,
            currentEmployeeId: draft.employee?.id,
            checkTime: !!selectedAppointmentEvent?.start && isSameDay(draft.startTime, selectedAppointmentEvent.start),
        })
        : employees.map((employee) => ({ employee, unavailableReason: null }));

    const onProviderChange = (employeeId: string) => {
        const employee = employees.find((em) => em.id.toString() === employeeId);
        if (!employee || !draft || employee.id === draft.employee?.id) return;

        const { services, missing } = swapServicesToEmployee(draft.services, employee);
        if (missing.length > 0) {
            toast.error(`${employee.name} doesn't offer ${missing.join(", ")}. Remove it first or pick another provider.`);
            return;
        }
        changeEmployee(employee, services);
    };

    if (!show || !checkoutAppointment) return null;


    return (<div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end transition-all">
        <div
            className="w-full max-w-lg bg-white shadow-2xl overflow-y-auto transform transition-transform duration-300 ease-in-out">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
                <h2 className="text-xl font-semibold text-gray-900">{showAsClosed ? 'Closed' : 'Checkout'}</h2>
                <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                >
                    <X className="w-5 h-5 text-gray-600" />
                </button>
            </div>

            {/* Client Info */}
            <div className="flex justify-between p-6 border-b border-gray-200">
                <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold text-lg">
                            {checkoutAppointment.patientName
                                .split(" ")
                                .map((n) => n[0])
                                .join("")}
                        </span>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                            {checkoutAppointment.patientName}
                        </h3>
                        <p className="text-sm text-gray-500">Client Since &nbsp;
                            {checkoutAppointment?.createdAt && format(checkoutAppointment?.createdAt, "M.dd.yyyy")}
                        </p>
                    </div>
                </div>

                {/*{isOpen && (isEditing ? <button onClick={handleUpdateAppointment}>{isSaving ? 'Updating..' : 'Update'}</button> : <button onClick={()=>setIsEditing(true)}>Edit</button>)}*/}
            </div>

            {/* Appointment Notes */}
            <div className="px-6 py-4 border-b border-gray-200 bg-amber-50 group">
                <div className="flex justify-between items-center mb-1">
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Notes</p>
                    {!isClosed && !isEditingNotes && (
                        <button
                            onClick={() => setIsEditingNotes(true)}
                            className="p-1 hover:bg-amber-100 rounded text-amber-700 transition-colors"
                        >
                            <Edit2 className="w-3.5 h-3.5" />
                        </button>
                    )}
                    {!isClosed && isEditingNotes && (
                        <button
                            onClick={() => {
                                updateNotes(checkoutAppointment.notes ?? '');
                                setIsEditingNotes(false);
                            }}
                            className="p-1 hover:bg-amber-100 rounded text-amber-700 transition-colors"
                        >
                            <Check className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>

                {isEditingNotes ? (
                    <textarea
                        autoFocus
                        disabled={isClosed}
                        value={checkoutAppointment.notes ?? ''}
                        onChange={(e) => {
                            setCheckoutAppointment({ ...checkoutAppointment, notes: e.target.value });
                        }}
                        onBlur={(e) => {
                            updateNotes(e.target.value);
                            setIsEditingNotes(false);
                        }}
                        rows={3}
                        placeholder="Add a note..."
                        className="w-full text-sm text-amber-900 bg-transparent placeholder-amber-400 resize-none focus:outline-none"
                    />
                ) : (
                    <p
                        onClick={() => { if (!isClosed) setIsEditingNotes(true); }}
                        className={`text-sm text-amber-900 bg-transparent ${!checkoutAppointment.notes ? 'italic text-amber-600 cursor-pointer' : 'cursor-pointer'}`}
                    >
                        {checkoutAppointment.notes || 'Add a note...'}
                    </p>
                )}
            </div>

            {/* Appointment Details */}
            <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Appointment Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Date */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Date
                        </label>

                        <input
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"

                            type="date"
                            disabled={showAsClosed}
                            value={format(draft?.startTime as Date, "yyyy-MM-dd")}
                            onChange={e => updateDate(e.target.value)}
                        />


                    </div>

                    {/* Start Time */}
                    <div>
                        {draft && (
                            <AvailableSlotSelect
                                events={events}
                                resourceId={draft.employee?.id}
                                date={draft.startTime}
                                slotInterval={10}
                                selectedSlot={format(draft.startTime ?? new Date(), "HH:mm")}
                                onSelect={updateTime}
                                duration={differenceInMinutes(draft.endTime ?? new Date(), draft.startTime ?? new Date())}
                                ignoreEventId={draft.appointmentId}
                                disabled={showAsClosed}
                            />
                        )}


                    </div>

                    {/* Practitioner */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Provider
                        </label>

                        <select
                            disabled={!canChangeProvider}
                            required
                            value={draft?.employee?.id ?? selectedAppointmentEvent?.employee?.id ?? selectedAppointmentEvent?.appointment?.employeeId ?? ""}
                            onChange={(e) => onProviderChange(e.target.value)}
                            className="w-full border px-3 py-2 rounded focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Select practitioner</option>
                            {providerOptions.map(({ employee: em, unavailableReason }) => (
                                <option key={em.id} value={em.id} disabled={!!unavailableReason}>
                                    {unavailableReason ? `${em.name} (${unavailableReason})` : em.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="mt-3 text-sm text-gray-600">
                    {/*Duration: {checkoutAppointment.duration} minutes • End time:{" "}*/}
                    {/*{checkoutAppointment.endTime}*/}

                    Duration: {differenceInMinutes(draft?.endTime ?? new Date(), draft?.startTime ?? new Date())} minutes
                    •
                    End time: {format(draft?.endTime ?? new Date(), "hh:mm a")}
                </div>
            </div>

            {/* Checkout Items */}
            <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
                {checkoutItems.map((item) => (<div
                    key={`${item.id}-${item.type}`}
                    className="flex items-center justify-between py-3 border-b border-gray-100"
                >
                    <div className="flex items-center space-x-3">
                        <div
                            className={`w-1 h-10 rounded-full ${item.type === "service" ? "bg-blue-500" : "bg-green-500"}`}
                        />
                        <div>
                            <h4 className="font-medium text-gray-900 text-sm">{item.name}</h4>
                            {item.practitioner && (
                                <p className="text-xs text-gray-500">with {item.practitioner}</p>)}
                            {item.category && (<p className="text-xs text-gray-500">{item.category}</p>)}
                            {item.type === "product" && isOpen &&
                                <div className="flex items-center space-x-2 mt-1">
                                    <button
                                        disabled={isClosed}
                                        onClick={() => removeProduct(item)}
                                        className="w-5 h-5 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center text-xs"
                                    >
                                        -
                                    </button>
                                    <span className="text-xs font-medium min-w-[40px] text-center">
                                        Qty: {item.quantity}
                                    </span>
                                    <button
                                        disabled={isClosed}
                                        onClick={() => addProduct(item)}
                                        className="w-5 h-5 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center text-xs"
                                    >
                                        +
                                    </button>
                                </div>}
                        </div>
                    </div>
                    <div className="flex items-center space-x-3">
                        <span className="text-sm font-semibold text-gray-900">
                            ${(item?.price * item?.quantity).toFixed(2)}
                        </span>
                        {isOpen && <button
                            disabled={isClosed}
                            onClick={() => removeItemFromCheckout(item, item.type)}
                            className="text-red-400 hover:text-red-600 p-1"
                        >
                            <X className="w-3 h-3" />
                        </button>}
                    </div>
                </div>))}
            </div>

            {/* Add Buttons */}
            {(isOpen) && <div className="p-4 space-y-2">
                <button type={'button'} onClick={() => handleServiceModal(true)}
                    className="flex items-center space-x-3 text-gray-600 hover:text-blue-600 transition-colors w-full">
                    <div
                        className="w-8 h-8 border-2 border-gray-300 hover:border-blue-500 rounded-full flex items-center justify-center transition-colors">
                        <Plus className="w-4 h-4" />
                    </div>
                    <span className="cursor-pointer text-sm font-medium">ADD SERVICE</span>
                </button>

                <button type={'button'} onClick={() => handleProductModal(true)}
                    className="flex items-center space-x-3 text-gray-600 hover:text-blue-600 transition-colors w-full">
                    <div
                        className="w-8 h-8 border-2 border-gray-300 hover:border-blue-500 rounded-full flex items-center justify-center transition-colors">
                        <Plus className="w-4 h-4" />
                    </div>
                    <span className="cursor-pointer text-sm font-medium">ADD PRODUCT</span>
                </button>
            </div>}

            {/* Footer */}
            <div className="border-t border-gray-200 bg-white p-6">
                <div className={`flex justify-between items-center ${showAsClosed ? "mb-4" : "mb-2"}`}>
                    <span className="text-lg font-medium text-gray-900">Subtotal</span>
                    <span className="text-lg font-semibold text-gray-900">
                        ${calculateSubtotal().toFixed(2)}
                    </span>
                </div>
                {!showAsClosed && (
                    <div className="mb-4 space-y-1 text-sm">
                        <div className="flex justify-between text-gray-700">
                            <span>Tax</span>
                            <span className="font-medium">{formatCents(checkoutTaxCents)}</span>
                        </div>
                        <div className="flex justify-between text-gray-900">
                            <span>Total</span>
                            <span className="font-semibold">{formatCents(breakdown.totalCents)}</span>
                        </div>
                        {breakdown.hasPriorPayment && (
                            <>
                                <div className="flex justify-between text-gray-700">
                                    <span>Paid</span>
                                    <span className="font-medium">-{formatCents(breakdown.paidCents)}</span>
                                </div>
                                {paidExtrasLabel(breakdown.paidCardFeeCents, breakdown.paidTipsCents) && (
                                    <div className="text-xs text-gray-400">
                                        {paidExtrasLabel(breakdown.paidCardFeeCents, breakdown.paidTipsCents)}
                                    </div>
                                )}
                                <div className="flex justify-between text-amber-700">
                                    <span>Pending</span>
                                    <span className="font-semibold">{formatCents(breakdown.pendingCents)}</span>
                                </div>
                            </>
                        )}
                        {breakdown.cardFeePercentage > 0 && breakdown.pendingCents > 0 && (
                            <div className="text-right text-xs text-gray-500">
                                {formatCents(breakdown.pendingByCardCents)} if paid by card ({breakdown.cardFeePercentage}% fee)
                            </div>
                        )}
                    </div>
                )}

                {showAsClosed && paidSummary && (
                    <div className="space-y-1 text-sm">
                        <div className="flex justify-between text-gray-700">
                            <span>Tax</span>
                            <span className="font-medium">{formatCents(paidSummary.taxCents)}</span>
                        </div>
                        {paidSummary.discountCents > 0 && (
                            <div className="flex justify-between text-gray-700">
                                <span>Discount</span>
                                <span className="font-medium">-{formatCents(paidSummary.discountCents)}</span>
                            </div>
                        )}
                        <div className="flex justify-between text-gray-900">
                            <span>Total</span>
                            <span className="font-semibold">{formatCents(paidSummary.totalCents)}</span>
                        </div>
                        <div className="flex justify-between text-gray-900 pt-2">
                            <span className="font-medium">Paid</span>
                            <span className="font-semibold">{formatCents(paidSummary.paidCents)}</span>
                        </div>
                        {paidSummary.payments.map((payment: any, index: number) => (
                            <div key={index} className="flex justify-between text-xs text-gray-500 pl-3">
                                <span>{payment.label} · {payment.method}</span>
                                <span>{formatCents(payment.amountCents)}</span>
                            </div>
                        ))}
                        {paidExtrasLabel(paidSummary.cardFeeCents, paidSummary.tipCents) && (
                            <div className="text-xs text-gray-400 pl-3">
                                {paidExtrasLabel(paidSummary.cardFeeCents, paidSummary.tipCents)}
                            </div>
                        )}
                    </div>
                )}

                {(isOpen && !showAsClosed) && (
                    <>
                        <button
                            onClick={proceedToPayment}
                            className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg rounded-lg transition-colors shadow-lg"
                        >
                            Continue to Payment
                        </button>
                        <div className="mt-4 flex justify-center gap-6">
                            {canMarkNoShow && (
                                <button
                                    type="button"
                                    onClick={() => setShowNoShowConfirm(true)}
                                    className="text-sm font-medium text-orange-600 hover:text-orange-700 transition-colors"
                                >
                                    Mark as No-Show
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => setShowCancelConfirm(true)}
                                className="text-sm font-medium text-red-600 hover:text-red-700 transition-colors"
                            >
                                Cancel Appointment
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>


        {addServiceModal && <ServiceModal onHandleAddService={addService} selectedEvent={selectedAppointmentEvent}
            selectedServices={selectedServices}
            services={selectedEmployee?.services ?? []}
            isOpen={addServiceModal}
            onClose={() => handleServiceModal(false)} />}
        {addProductModal &&
            <ProductModal products={products?.categories} onRemoveProduct={removeProduct} onAddProduct={addProduct}
                selectedProducts={draft?.products} isOpen={addProductModal}
                onClose={() => handleProductModal(false)} />}

        {showNoShowConfirm && checkoutAppointment && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4">
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-gray-900">Mark as No-Show?</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            <span className="font-medium text-gray-700">{checkoutAppointment.patientName}</span>{' '}
                            didn't show up. {waiveNoShowFee ? "Nothing will be charged. Any deposit is kept." : noShowPolicyText}
                        </p>
                    </div>
                    {canWaiveNoShowFee && account?.noShowPolicy && account.noShowPolicy !== "NONE" && (
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                                id="waiveNoShowFee"
                                type="checkbox"
                                checked={waiveNoShowFee}
                                onChange={(e) => setWaiveNoShowFee(e.target.checked)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                            Waive the no-show fee
                        </label>
                    )}
                    <div className="flex space-x-3 pt-2">
                        <button
                            onClick={() => {
                                setShowNoShowConfirm(false);
                                setWaiveNoShowFee(false);
                            }}
                            disabled={markingNoShow}
                            className="flex-1 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Go Back
                        </button>
                        <button
                            onClick={() => markNoShow(checkoutAppointment.id)}
                            disabled={markingNoShow}
                            className="flex-1 py-2.5 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-medium rounded-lg transition-colors"
                        >
                            {markingNoShow ? "Marking…" : "Mark No-Show"}
                        </button>
                    </div>
                </div>
            </div>
        )}
        {showCancelConfirm && checkoutAppointment && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4">
                    <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto">
                        <X className="w-6 h-6 text-red-600" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-gray-900">Cancel Appointment?</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            This will permanently remove the appointment for{' '}
                            <span className="font-medium text-gray-700">{checkoutAppointment.patientName}</span>.
                            This action cannot be undone.
                        </p>
                        {hasLateCancelFee && (
                            <p className="mt-2 text-sm text-gray-700">
                                It's within {account?.clientChangeCutoffHours} hours of the start, so the card on file
                                can be charged {lateCancelText}, less any deposit.
                            </p>
                        )}
                    </div>
                    {hasLateCancelFee ? (
                        <div className="flex flex-col gap-2 pt-2">
                            <button
                                onClick={() => {
                                    setShowCancelConfirm(false);
                                    deleteAppointment(checkoutAppointment.id, "charge");
                                }}
                                className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
                            >
                                Cancel and Charge Fee
                            </button>
                            {currentUser?.role === UserRoles.ADMIN && (
                                <button
                                    onClick={() => {
                                        setShowCancelConfirm(false);
                                        deleteAppointment(checkoutAppointment.id, "waive");
                                    }}
                                    className="w-full py-2.5 border border-red-300 text-red-700 font-medium rounded-lg hover:bg-red-50 transition-colors"
                                >
                                    Cancel and Waive Fee
                                </button>
                            )}
                            <button
                                onClick={() => setShowCancelConfirm(false)}
                                className="w-full py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Keep It
                            </button>
                        </div>
                    ) : (
                    <div className="flex space-x-3 pt-2">
                        <button
                            onClick={() => setShowCancelConfirm(false)}
                            className="flex-1 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Keep It
                        </button>
                        <button
                            onClick={() => {
                                setShowCancelConfirm(false);
                                deleteAppointment(checkoutAppointment.id);
                            }}
                            className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
                        >
                            Yes, Cancel
                        </button>
                    </div>
                    )}
                </div>
            </div>
        )}
    </div>);
};

export default CheckoutPanel;
